import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, ChevronRight } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { WingmanChat } from "@/components/WingmanChat";
import { PdfTool } from "@/components/PdfTool";
import { TOOL_BY_SLUG, SITE_URL } from "@/data/tools";
import { trackEvent } from "@/utils/analytics";

const EASE = [0.16, 1, 0.3, 1];

const DEFAULTS = {
  title: "PDF Tools for M&A & Legal Teams — Compress, Merge, Redact | Doclyx",
  description:
    "PDF tools for M&A and legal teams — compress to exact sizes, merge, redact permanently, convert, protect. Zero file retention. First conversion free.",
  canonical: `${SITE_URL}/`,
};

const setMeta = (selector, attr, content) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(selector.startsWith("link") ? "link" : "meta");
    if (selector.includes('name="')) el.setAttribute("name", selector.match(/name="([^"]+)"/)[1]);
    if (selector.includes('property="')) el.setAttribute("property", selector.match(/property="([^"]+)"/)[1]);
    if (selector.startsWith("link")) el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute(attr, content);
};

const useToolMeta = (tool) => {
  useEffect(() => {
    const url = `${SITE_URL}/${tool.slug}`;
    document.title = tool.title;
    setMeta('meta[name="description"]', "content", tool.description);
    setMeta('link[rel="canonical"]', "href", url);
    setMeta('meta[property="og:title"]', "content", tool.title);
    setMeta('meta[property="og:description"]', "content", tool.description);
    setMeta('meta[property="og:url"]', "content", url);
    setMeta('meta[property="og:image"]', "content", `${SITE_URL}/og/${tool.slug}.jpg`);
    setMeta('meta[name="twitter:image"]', "content", `${SITE_URL}/og/${tool.slug}.jpg`);

    const faqLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: tool.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
    document.getElementById("tool-faq-ld")?.remove();
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "tool-faq-ld";
    ld.textContent = JSON.stringify(faqLd);
    document.head.appendChild(ld);

    return () => {
      document.title = DEFAULTS.title;
      setMeta('meta[name="description"]', "content", DEFAULTS.description);
      setMeta('link[rel="canonical"]', "href", DEFAULTS.canonical);
      setMeta('meta[property="og:title"]', "content", DEFAULTS.title);
      setMeta('meta[property="og:description"]', "content", DEFAULTS.description);
      setMeta('meta[property="og:url"]', "content", DEFAULTS.canonical);
      setMeta('meta[property="og:image"]', "content", `${SITE_URL}/doclyx-social-v7.png`);
      setMeta('meta[name="twitter:image"]', "content", `${SITE_URL}/doclyx-social-v7.png`);
      document.getElementById("tool-faq-ld")?.remove();
    };
  }, [tool]);
};

export default function ToolPage({ slug }) {
  const tool = TOOL_BY_SLUG[slug];
  useToolMeta(tool);

  useEffect(() => {
    trackEvent("page_view", { source: slug });
  }, [slug]);

  return (
    <main className="bg-page text-ink min-h-screen" data-testid="tool-page">
      <Nav />

      <section className="pt-32 md:pt-40 pb-10 md:pb-14 px-6 md:px-12">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE }}
            className={tool.engine ? "lg:col-span-5" : "lg:col-span-8"}
          >
            <nav className="font-mono text-[11px] uppercase tracking-[0.2em] text-mist mb-5" data-testid="tool-breadcrumb">
              <Link to="/" className="hover:text-azure transition-colors duration-300">Home</Link>
              <ChevronRight className="inline w-3 h-3 mx-1 -mt-0.5" strokeWidth={2} />
              <Link to="/engines" className="hover:text-azure transition-colors duration-300">Engines</Link>
              <ChevronRight className="inline w-3 h-3 mx-1 -mt-0.5" strokeWidth={2} />
              <span className="text-fog">{tool.h1}</span>
            </nav>
            <h1 className="font-medium tracking-tight leading-tight text-4xl md:text-5xl mb-5" data-testid="tool-title">
              {tool.h1}
            </h1>
            <p className="text-base text-fog leading-relaxed mb-6" data-testid="tool-intro">{tool.intro}</p>
            <div className="flex flex-wrap gap-2 mb-8">
              {["First conversion free", "No account needed", "Files never stored"].map((chip) => (
                <span key={chip} className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-azure border border-azure/30 rounded-full px-3 py-1.5">
                  <Check className="w-3 h-3" strokeWidth={2} /> {chip}
                </span>
              ))}
            </div>
            {tool.variantLinks && (
              <div className="mb-8" data-testid="tool-variants">
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-mist block mb-2">Compress to an exact size</span>
                <div className="flex flex-wrap gap-2">
                  {tool.variantLinks.map((v) => (
                    <Link
                      key={v}
                      to={`/${v}`}
                      data-testid={`tool-variant-${v}`}
                      className="font-mono text-xs text-fog border border-hairline rounded-full px-3.5 py-1.5 hover:text-azure hover:border-azure/40 transition-colors duration-300"
                    >
                      {TOOL_BY_SLUG[v].h1.replace("Compress PDF to ", "")}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </motion.div>

          {tool.engine && (
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: EASE, delay: 0.15 }}
              className="lg:col-span-7"
              data-testid="tool-instrument"
            >
              <PdfTool key={tool.slug} guest light maxMb={10} initialAction={tool.engine} initialParams={tool.initialParams || null} />
            </motion.div>
          )}
        </div>
      </section>

      {tool.table && (
        <section className="py-12 md:py-16 px-6 md:px-12 border-t border-hairline">
          <div className="max-w-4xl mx-auto">
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-mist block mb-8">Side by side</span>
            <div className="bg-white border border-hairline rounded-sm overflow-hidden" data-testid="tool-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-hairline">
                    {tool.table.head.map((h, i) => (
                      <th key={i} className={`text-left font-mono text-[10px] uppercase tracking-[0.15em] px-5 py-4 ${i === 2 ? "text-azure" : "text-mist"}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tool.table.rows.map((row, i) => (
                    <tr key={i} className="border-b border-hairline last:border-0" data-testid={`tool-table-row-${i}`}>
                      {row.map((cell, j) => (
                        <td key={j} className={`px-5 py-4 align-top leading-relaxed ${j === 0 ? "font-medium text-ink" : j === 2 ? "text-ink" : "text-fog"}`}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {tool.sections && (
        <section className="py-12 md:py-16 px-6 md:px-12 border-t border-hairline">
          <div className="max-w-3xl mx-auto space-y-10" data-testid="tool-sections">
            {tool.sections.map((s, i) => (
              <motion.div
                key={s.heading}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, ease: EASE, delay: i * 0.05 }}
                data-testid={`tool-section-${i}`}
              >
                <h2 className="text-xl md:text-2xl font-medium tracking-tight text-ink mb-4">{s.heading}</h2>
                {s.paras.map((p, j) => (
                  <p key={j} className="text-base text-fog leading-relaxed mb-4 last:mb-0">{p}</p>
                ))}
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {tool.cta && (
        <section className="px-6 md:px-12 pb-12 md:pb-16">
          <div className="max-w-3xl mx-auto">
            <Link
              to={tool.cta.href}
              data-testid="tool-cta"
              className="group flex items-center justify-between bg-azure text-white rounded-full px-6 md:px-8 py-5 md:py-6 hover:-translate-y-0.5 transition-transform duration-500"
            >
              <span className="text-base md:text-lg font-medium tracking-tight">{tool.cta.label}</span>
              <ArrowUpRight className="w-5 h-5 shrink-0 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
            </Link>
          </div>
        </section>
      )}

      {tool.steps && (
        <section className="py-12 md:py-16 px-6 md:px-12 border-t border-hairline">
          <div className="max-w-7xl mx-auto">
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-mist block mb-8">How it works</span>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6" data-testid="tool-steps">
              {tool.steps.map((step, i) => (
                <motion.div
                  key={step}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.7, ease: EASE, delay: i * 0.08 }}
                  className="bg-white border border-hairline rounded-sm p-6"
                  data-testid={`tool-step-${i}`}
                >
                  <span className="font-mono text-[10px] tracking-[0.2em] text-azure block mb-3">{String(i + 1).padStart(2, "0")}</span>
                  <p className="text-sm text-fog leading-relaxed">{step}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-12 md:py-16 px-6 md:px-12 border-t border-hairline">
        <div className="max-w-3xl mx-auto">
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-mist block mb-8">Common questions</span>
          <div className="space-y-4" data-testid="tool-faq">
            {tool.faqs.map((f, i) => (
              <motion.div
                key={f.q}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, ease: EASE, delay: i * 0.06 }}
                className="bg-white border border-hairline rounded-sm p-6"
                data-testid={`tool-faq-${i}`}
              >
                <h2 className="text-base font-medium tracking-tight text-ink mb-2">{f.q}</h2>
                <p className="text-sm text-fog leading-relaxed">{f.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 px-6 md:px-12 border-t border-hairline">
        <div className="max-w-7xl mx-auto">
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-mist block mb-8">Related tools</span>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="tool-related">
            {tool.related.filter((rel) => TOOL_BY_SLUG[rel]).map((rel) => {
              const r = TOOL_BY_SLUG[rel];
              return (
                <Link
                  key={rel}
                  to={`/${rel}`}
                  data-testid={`tool-related-${rel}`}
                  className="group bg-white border border-hairline rounded-sm p-6 hover:-translate-y-1 transition-transform duration-500"
                >
                  <p className="text-base font-medium tracking-tight text-ink mb-1 flex items-center justify-between">
                    {r.h1}
                    <ArrowUpRight className="w-4 h-4 text-mist group-hover:text-azure group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" />
                  </p>
                  <p className="text-sm text-fog leading-relaxed">{r.description.split(".")[0]}.</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
      <WingmanChat />
    </main>
  );
}
